import { ListCardFields } from '@resource-kit/ui/react';
import type { ListCardRenderer } from '@resource-kit/controllers/react';
import type { FulfillmentJob } from '@/services';
import { monthDay } from '../phone/phoneSearchModel';
import { resolveJobOrderName } from '../widgets';

/** Items the card does not show as body fields: the date rides the title. */
const NOT_IN_BODY: ReadonlySet<string> = new Set(['delivery_date', 'onfleet_task_short_id']);

/**
 * The delivery job card of the jobs list (#2680).
 *
 * The title is the order name with the delivery date beside it as `MM/DD`.
 * The order name is `shopify_order_name`, not the `name` column: the backend
 * composes `name` as `<order>-<MMDD>`, which would show the date twice. When
 * the order name is null, the `name` item content is the title. The body is
 * every other visible column. The Onfleet short id is left out:
 * it is an Onfleet handle, not a field an operator reads on a phone. A hidden
 * column stays hidden: no date when `delivery_date` is not among the items,
 * and the next item is the title when `name` is not.
 *
 * Called as a plain function by the kit, not rendered: no hooks.
 */
export const renderJobCard: ListCardRenderer<FulfillmentJob> = ({ row, items }) => {
  const rest = items.filter((item) => !NOT_IN_BODY.has(item.id));
  const nameItem = rest.find((item) => item.id === 'name');
  const title = nameItem ?? rest[0];
  const orderName = nameItem ? resolveJobOrderName(row) : null;
  const body = rest.filter((item) => item !== title);
  const date =
    items.some((item) => item.id === 'delivery_date') &&
    typeof row.delivery_date === 'string' &&
    row.delivery_date !== ''
      ? row.delivery_date
      : null;

  return {
    title: (
      <span className="inline-flex min-w-0 flex-wrap items-baseline gap-2">
        {orderName !== null ? (
          <span className="font-mono text-xs">{orderName}</span>
        ) : (
          title?.content
        )}
        {date !== null && (
          <span data-testid="job-card-date" className="text-xs text-muted-foreground">
            {monthDay(date)}
          </span>
        )}
      </span>
    ),
    body: <ListCardFields items={body} />,
  };
};
